/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  AppError,
  TSmartCarouselScreenGetParams
} from "@tivocorp/data-domain-lib";

import { HomeScreenInteractor } from "../../model/interactor";
import { PostSigninInteractor } from "../../model/interactor";
import { DiagnosticLogger, Log, OperationalLogger } from "../../model/logging";
import { DiagnosticSeverity } from "../../model/types";
import type { QuickMenuShortcutV4 } from "../../model/types";
import { RetryMechanism } from "../../model/retrymechanism/RetryMechanism";
import type { TCarouselRail } from "../../model/types";
import { RETRY_POLICY } from "../../model/types";
import { HomeScreenDimmerState } from "./HomeScreenDimmerState";
import {
  applyCursorPage,
  applyOffsetPage,
  canLoadMoreCursor,
  CAROUSEL_TILE_PAGE_SIZE
} from "../utility/carouselPagination";
import {
  createLoadMoreTilesThunk,
  isNetworkError,
  TLoadMoreTilesPayload
} from "../utility/carouselThunkFactory";
import { HomescreenFocusedField } from "./homescreenTypes";
import type {
  HomescreenState,
  TCarouselRailPage,
  TCarouselRailPaginationState,
  TTilePaginationState
} from "./homescreenTypes";
import type { UnityRootState, UnityAppDispatch } from "../UnityAppStore";
import {
  CAROUSEL_PAGE_SIZE,
  CAROUSEL_SCREEN_NAME,
  HOMESCREEN_CAROUSEL_API_ENDPOINT,
  HOMESCREEN_CAROUSEL_SCREEN_ID,
  HOMESCREEN_CAROUSEL_SCREEN_NAME
} from "./homescreenConstants";
import {
  QUICK_MENU_API_ENDPOINT,
  QUICK_MENU_DIAGNOSTIC_EVENT
} from "../../model/constants/quickMenuTelemetryConstants";
import {
  mapCarouselsResponseToInitialTilePagination,
  mapCarouselsResponseToPage
} from "./homescreenMappers";
import {
  HomeScreenErrorMapper,
  HOMESCREEN_ERROR_CODE
} from "./homeScreenErrorMapper";
import { DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS } from "../../config";
import {
  getImageHostBaseUrl,
  clearSmartCarouselApiCache,
  buildScreenGetParams,
  fetchCarouselTilePage
} from "../utility/smartCarouselUtility";
import { CarouselTemplate } from "../../model/enums";

const TAG: string = "homescreenSlice";
const UNKNOWN_ERROR_MESSAGE: string = "Unknown error";

/** Operational logger bound to the Home Screen's carousel endpoint. */
const HomeScreenOperationalLogger = OperationalLogger.create({
  screenId: HOMESCREEN_CAROUSEL_SCREEN_ID,
  apiEndpoint: HOMESCREEN_CAROUSEL_API_ENDPOINT
});

const QuickMenuOperationalLogger = OperationalLogger.create({
  screenId: HOMESCREEN_CAROUSEL_SCREEN_ID,
  apiEndpoint: QUICK_MENU_API_ENDPOINT
});

/** Diagnostic event name for the Home Screen carousel lifecycle. */
const DIAGNOSTIC_EVENT: string = "homescreen_carousel";

const INITIAL_STATE: HomescreenState = {
  carouselsPagination: {
    items: [],
    pageCursor: null,
    isLoading: false,
    error: "",
    errorCode: ""
  },
  tilePagination: {},
  focusedCarouselIndex: 0,
  focusedTileIndices: {},
  refreshIntervalSecs: DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS,
  lastFetchTimestamp: 0,
  carouselsProfileId: "",
  // Shortcut state
  isLoadingShortcuts: false,
  isShortcutsLoaded: false,
  shortcutsError: "",
  quickMenuShortcuts: [],
  isNpvrRecordingAllowed: undefined,
  focusedShortcutIndex: 0,
  // Focus routing
  focusedField: HomescreenFocusedField.MENU_STRIP,
  // Dimmer state
  dimmerState: HomeScreenDimmerState.SEMI
};

// ─── Shared Helpers ───

/**
 * Clears cached values. Intended for use during sign-out or when
 * TSRI endpoints may have changed. Callers must wire this into their
 * sign-out flow explicitly.
 */
export const clearHomescreenCache = (): void => {
  clearSmartCarouselApiCache();
};

function isRwcAvailable(state: HomescreenState): boolean {
  const firstCarousel = state.carouselsPagination.items[0];
  return (
    firstCarousel !== undefined &&
    firstCarousel.template === CarouselTemplate.LOGO_PIP_LANDSCAPE_HORIZONTAL
  );
}

/**
 * Derives the dimmer state from the current focus.
 *
 * SEMI when focus is on the menu strip, profile icon, now-playing tile, or the
 * first carousel when RWC is present. FULL when focus is on any other carousel or tile.
 *
 */

function deriveDimmerState(
  state: HomescreenState,
  focusedField: HomescreenFocusedField,
  focusedCarouselIndex: number
): HomeScreenDimmerState {
  if (focusedField === HomescreenFocusedField.MENU_STRIP) {
    return HomeScreenDimmerState.SEMI;
  }
  if (focusedField === HomescreenFocusedField.PROFILE_ICON) {
    return HomeScreenDimmerState.SEMI;
  }
  if (focusedField === HomescreenFocusedField.NOW_PLAYING_TILE) {
    return HomeScreenDimmerState.SEMI;
  }
  if (
    focusedField === HomescreenFocusedField.CAROUSELS &&
    focusedCarouselIndex === 0 &&
    isRwcAvailable(state)
  ) {
    return HomeScreenDimmerState.SEMI;
  }
  return HomeScreenDimmerState.FULL;
}

// ─── Param Builders ───

// ─── Async Thunks ───

interface InitialCarouselsPayload {
  page: TCarouselRailPage;
  tilePagination: Record<string, TTilePaginationState>;
  refreshIntervalSecs: number;
}

/**
 * Rejection payload for the initial carousels load.
 *
 * `errorCode` is the composite catalog code the A707 dialog renders —
 * `"A707-G500"`, or `"A707"` when there is no sub-error code to show. Both
 * fields are empty for an intentional abort, which shows nothing.
 */
interface CarouselsErrorPayload {
  message: string;
  errorCode: string;
}

/**
 * One attempt at the screens/recommendations call: resolve the image host,
 * build the params, fetch, and map the response into carousel state.
 *
 * A 200 OK with no displayable content (empty predictions) throws the bare
 * A707 error so it travels the same path as a failed call.
 *
 * Shared by every caller that loads the initial page — the first attempt, the
 * background retry loop, and the dialog's one-shot Retry — so all three fail
 * and succeed identically.
 */
const fetchInitialCarousels = async (
  signal: AbortSignal
): Promise<InitialCarouselsPayload> => {
  // Logged per attempt, before anything can throw: a retry that fails at the
  // network gate or in param validation never reaches the wire, so this is the
  // only place the attempt is visible at all.
  Log.d(TAG, "fetchInitialCarousels() - Attempt starting");
  const interactor: PostSigninInteractor = new PostSigninInteractor();
  const imageHostBaseUrl: string = await getImageHostBaseUrl(interactor);
  const params: TSmartCarouselScreenGetParams = await buildScreenGetParams(
    CAROUSEL_SCREEN_NAME,
    CAROUSEL_PAGE_SIZE,
    CAROUSEL_TILE_PAGE_SIZE,
    null
  );
  const response = await interactor.fetchScreen(params, { signal });
  if (HomeScreenErrorMapper.isEmptyResponse(response.carousels)) {
    Log.w(TAG, "fetchInitialCarousels() - Empty carousels response");
    DiagnosticLogger.log(
      DIAGNOSTIC_EVENT,
      "Screens call returned no displayable carousels",
      DiagnosticSeverity.Debug,
      {
        screen_id: HOMESCREEN_CAROUSEL_SCREEN_ID,
        event: "empty_predictions",
        carousel_count: String(
          response.carousels === undefined || response.carousels === null
            ? 0
            : response.carousels.length
        )
      }
    );
    throw HomeScreenErrorMapper.fromEmptyResponse();
  }
  return {
    page: mapCarouselsResponseToPage(response, imageHostBaseUrl),
    tilePagination: mapCarouselsResponseToInitialTilePagination(
      response,
      imageHostBaseUrl
    ),
    refreshIntervalSecs:
      response.refreshIntervalSecs === undefined ||
      response.refreshIntervalSecs === null
        ? DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS
        : response.refreshIntervalSecs
  };
};

/**
 * Turns a mapped A707 error into the payload the reducer stores. The message
 * comes from the AppError so a domain error — which does not extend `Error` —
 * still reports its own text rather than a generic fallback.
 */
const toCarouselsErrorPayload = (
  appError: AppError,
  fallbackMessage: string
): CarouselsErrorPayload => {
  const message: string = appError.getMessage();
  return {
    message: message.length > 0 ? message : fallbackMessage,
    errorCode: appError.getUIErrorwithSubErrorCode()
  };
};

/**
 * Reports a failed carousels fetch to the operational logger — the always-on
 * channel fleet dashboards read — carrying the catalog code the user is
 * looking at so a support call can be matched to a row.
 *
 * `subErrorCode` is omitted rather than sent empty when no layer recognised
 * the error, which is the same distinction the dialog makes between `A707` and
 * `A707-G500`.
 */
const logCarouselsFailure = (
  error: unknown,
  appError: AppError,
  latencyMs?: number
): void => {
  const subErrorCode: string = appError.getSubErrorCode();
  HomeScreenOperationalLogger.logApiFailure(error, {
    errorCode: appError.getCode(),
    subErrorCode: subErrorCode.length > 0 ? subErrorCode : undefined,
    latencyMs
  });
};

/** Empty payload for an intentional abort — the reducer shows nothing for it. */
const abortedCarouselsPayload = (context: string): CarouselsErrorPayload => {
  Log.d(TAG, context);
  return { message: "", errorCode: "" };
};

/**
 * True when a rejection is a cancellation rather than a failure: the thunk's
 * own signal was aborted, or the transport reports one — axios/Sandwich
 * (`CanceledError` / `ERR_CANCELED`) or DOM/fetch (`AbortError`).
 *
 * The transport check covers a request cancelled by something other than this
 * thunk's signal. The mapper does not recognise a cancellation, so without it
 * the request would surface as a bare A707 dialog and start the CRITICAL retry.
 *
 * @param error Rejection value from the carousels request.
 * @param signal The thunk's abort signal.
 * @returns True when the rejection represents a cancellation.
 */
const isRequestCancellation = (
  error: unknown,
  signal: AbortSignal
): boolean => {
  if (signal.aborted) {
    return true;
  }
  if (error === null || error === undefined) {
    return false;
  }
  const candidate = error as { name?: unknown; code?: unknown };
  return (
    candidate.name === "CanceledError" ||
    candidate.name === "AbortError" ||
    candidate.code === "ERR_CANCELED"
  );
};

/**
 * Loads the screens/recommendations carousels, reporting the first failure and
 * healing from it on its own.
 *
 * Any failure of the first attempt — transport, service context, HTTP status,
 * or a 200 OK with no displayable content — rejects immediately with the
 * mapped A707 code, so the dialog appears at once rather than after a silent
 * retry loop. The CRITICAL retry then continues in the **background**: when an
 * attempt eventually succeeds it dispatches `updateCarouselsFromRetry`, which
 * fills the screen and clears the error code, taking the dialog down without
 * the user doing anything.
 *
 * Awaiting the CRITICAL loop instead (the previous behaviour) meant a server
 * error never settled the thunk at all — CRITICAL repeats its last interval
 * forever, so the rejection only arrived on abort, and no G4xx/G5xx code could
 * ever reach the dialog.
 *
 * The caller must provide an AbortSignal so the retry loop can be
 * cancelled when the user navigates away from HomeScreen.
 */
export const loadCarouselsWithRetry = createAsyncThunk<
  InitialCarouselsPayload,
  { signal: AbortSignal },
  { rejectValue: CarouselsErrorPayload }
>(
  "homescreen/loadCarouselsWithRetry",
  async (
    { signal }: { signal: AbortSignal },
    { dispatch, rejectWithValue }
  ) => {
    /**
     * Keeps retrying after the dialog is already on screen. Not awaited — the
     * thunk has rejected by the time this runs, so a success arrives through
     * `updateCarouselsFromRetry` instead of the fulfilled reducer.
     *
     * Under CRITICAL the loop only ends by abort (navigating away), which is
     * why the catch is a debug log rather than an error path.
     */
    const startBackgroundRetry = (): void => {
      // `executeWithRetry` checks the signal before its first attempt, so an
      // already-aborted signal means zero attempts — logged here to tell that
      // apart from the loop simply waiting out its interval.
      Log.i(
        TAG,
        `loadCarouselsWithRetry() - Starting background retry, aborted=${String(signal.aborted)}`
      );
      RetryMechanism.callServiceWithRetry<InitialCarouselsPayload>(
        fetchInitialCarousels as (
          ...args: Array<unknown>
        ) => Promise<InitialCarouselsPayload>,
        [signal],
        {
          retryPolicy: RETRY_POLICY.CRITICAL,
          httpMethod: "GET",
          // A later attempt that comes back empty ends the loop for the same
          // reason the first one never starts it: nothing failed, so there is
          // nothing to retry against.
          isTerminal: HomeScreenErrorMapper.isEmptyResponseError.bind(
            HomeScreenErrorMapper
          )
        },
        signal
      )
        .then((result: InitialCarouselsPayload): void => {
          Log.i(
            TAG,
            `loadCarouselsWithRetry() - Background retry succeeded, rails=${result.page.items.length}`
          );
          DiagnosticLogger.log(
            DIAGNOSTIC_EVENT,
            "Background retry recovered the carousels",
            DiagnosticSeverity.Debug,
            {
              screen_id: HOMESCREEN_CAROUSEL_SCREEN_ID,
              event: "background_retry_succeeded",
              rail_count: String(result.page.items.length)
            }
          );
          dispatch(updateCarouselsFromRetry(result));
        })
        .catch((retryError: unknown): void => {
          Log.d(
            TAG,
            "loadCarouselsWithRetry() - Background retry ended",
            retryError
          );
          DiagnosticLogger.log(
            DIAGNOSTIC_EVENT,
            "Background retry ended without recovering",
            DiagnosticSeverity.Debug,
            {
              screen_id: HOMESCREEN_CAROUSEL_SCREEN_ID,
              event: "background_retry_ended",
              aborted: String(signal.aborted)
            }
          );
          // Ended on empty predictions rather than by abort: report what the
          // screen is actually showing now — bare A707, nothing to retry.
          if (
            !signal.aborted &&
            HomeScreenErrorMapper.isEmptyResponseError(retryError)
          ) {
            const emptyError: AppError =
              HomeScreenErrorMapper.fromError(retryError);
            logCarouselsFailure(retryError, emptyError);
            dispatch(
              updateCarouselsErrorFromRetry(
                toCarouselsErrorPayload(emptyError, "Failed to load carousels")
              )
            );
          }
        });
    };

    const fetchStartTime: number = Date.now();
    try {
      Log.i(TAG, "loadCarouselsWithRetry() - Initial attempt");
      const result = await fetchInitialCarousels(signal);
      const fetchDuration = Date.now() - fetchStartTime;
      Log.d(
        TAG,
        `loadCarouselsWithRetry() - loaded rails=${result.page.items.length}, durationMs=${fetchDuration}`
      );
      DiagnosticLogger.log(
        DIAGNOSTIC_EVENT,
        "Initial carousels load succeeded",
        DiagnosticSeverity.Debug,
        {
          screen_id: HOMESCREEN_CAROUSEL_SCREEN_ID,
          event: "initial_load_succeeded",
          rail_count: String(result.page.items.length),
          latency_ms: String(fetchDuration)
        }
      );

      return result;
    } catch (firstError: unknown) {
      if (isRequestCancellation(firstError, signal)) {
        return rejectWithValue(
          abortedCarouselsPayload(
            `loadCarouselsWithRetry() - Cancelled (signalAborted=${String(signal.aborted)})`
          )
        );
      }

      // Start retrying before anything else touches the error. Mapping it to a
      // code is reporting, and reporting must never be what decides whether the
      // screen keeps trying to recover.
      //
      // Empty predictions are the one exception. Nothing failed — the service
      // has nothing to show yet — so polling it at the CRITICAL interval would
      // be traffic with no failure behind it. Report the bare A707 and stop.
      if (HomeScreenErrorMapper.isEmptyResponseError(firstError)) {
        Log.i(
          TAG,
          "loadCarouselsWithRetry() - Empty predictions, no background retry"
        );
      } else {
        startBackgroundRetry();
      }

      const appError: AppError = HomeScreenErrorMapper.fromError(firstError);
      const payload: CarouselsErrorPayload = toCarouselsErrorPayload(
        appError,
        isNetworkError(firstError)
          ? "Network error"
          : "Failed to load carousels"
      );
      Log.e(
        TAG,
        `loadCarouselsWithRetry() - Initial attempt failed, code=${payload.errorCode}`,
        firstError
      );
      logCarouselsFailure(firstError, appError, Date.now() - fetchStartTime);

      return rejectWithValue(payload);
    }
  }
);

/**
 * Fetches the next page of carousel rails using the saved pageCursor and
 * appends the new rails (plus their initial tile state) to existing state.
 * De-duplicated by canLoadMore() — no-ops when already loading or hasMore=false.
 */
export const loadMoreCarousels = createAsyncThunk<
  InitialCarouselsPayload,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "homescreen/loadMoreCarousels",
  async (_: void, { rejectWithValue, getState }) => {
    try {
      const cursor = getState().homescreen.carouselsPagination.pageCursor;
      const cursorForLog: string = cursor === null ? "null" : cursor;
      Log.i(TAG, `loadMoreCarousels() - cursor=${cursorForLog}`);

      const interactor: PostSigninInteractor = new PostSigninInteractor();
      const imageHostBaseUrl: string = await getImageHostBaseUrl(interactor);
      const params: TSmartCarouselScreenGetParams = await buildScreenGetParams(
        CAROUSEL_SCREEN_NAME,
        CAROUSEL_PAGE_SIZE,
        CAROUSEL_TILE_PAGE_SIZE,
        cursor
      );
      const fetchStartTime = Date.now();
      const response = await interactor.fetchScreen(params);
      const fetchDuration = Date.now() - fetchStartTime;

      const page = mapCarouselsResponseToPage(response, imageHostBaseUrl);
      Log.d(
        TAG,
        `loadMoreCarousels() - loaded rails=${page.items.length}, durationMs=${fetchDuration}`
      );

      return {
        page,
        tilePagination: mapCarouselsResponseToInitialTilePagination(
          response,
          imageHostBaseUrl
        ),
        refreshIntervalSecs:
          response.refreshIntervalSecs === undefined ||
          response.refreshIntervalSecs === null
            ? DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS
            : response.refreshIntervalSecs
      };
    } catch (error: unknown) {
      const message: string =
        error instanceof Error
          ? error.message
          : "Failed to load more carousels";
      Log.e(TAG, "loadMoreCarousels()", error);
      // No catalog code: a load-more failure appends nothing, it does not put
      // the A707 dialog up, so reporting a code here would misread as one.
      HomeScreenOperationalLogger.logApiFailure(error, {
        extra: { operation: "loadMoreCarousels" }
      });
      return rejectWithValue(message);
    }
  },
  {
    condition: (_arg: void, { getState }): boolean => {
      const state = getState();
      return canLoadMoreCursor(state.homescreen.carouselsPagination);
    }
  }
);

export const loadMoreTiles = createLoadMoreTilesThunk({
  thunkPrefix: "homescreen",
  tag: TAG,
  getTileState: (state: UnityRootState, carouselId: string) =>
    state.homescreen.tilePagination[carouselId],
  fetchPage: fetchCarouselTilePage,
  isSeeMoreNeeded: true
});

// ─── Slice ───

/**
 * Returns true only for shortcuts that navigate to a built-in screen.
 * ApplicationUiAction items (partner app launches) are not supported on the
 * Web Player and are stripped before being stored in Redux so that the D-pad
 * focus index and NAV_OK handler always operate on the same visible list.
 */
function isSupportedShortcut(shortcut: QuickMenuShortcutV4): boolean {
  return (
    shortcut.uiAction !== null &&
    shortcut.uiAction !== undefined &&
    shortcut.uiAction.uiActionType === "ScreenDestinationUiActionV4"
  );
}

function isMyHubShortcut(shortcut: QuickMenuShortcutV4): boolean {
  const action = shortcut.uiAction as { destination?: string };
  return action.destination === "myHub";
}

function getVisibleQuickMenuShortcuts(
  state: HomescreenState
): QuickMenuShortcutV4[] {
  if (state.isNpvrRecordingAllowed !== true) {
    return state.quickMenuShortcuts.filter(
      (shortcut: QuickMenuShortcutV4): boolean => !isMyHubShortcut(shortcut)
    );
  }
  return state.quickMenuShortcuts;
}

function getVisibleShortcutCount(state: HomescreenState): number {
  return getVisibleQuickMenuShortcuts(state).length;
}

function getMyHubShortcutIndex(state: HomescreenState): number {
  return state.quickMenuShortcuts.findIndex(isMyHubShortcut);
}

function clampFocusedShortcutIndex(state: HomescreenState): void {
  const visibleShortcutCount = getVisibleShortcutCount(state);
  if (visibleShortcutCount === 0) {
    state.focusedShortcutIndex = 0;
    return;
  }

  if (state.focusedShortcutIndex >= visibleShortcutCount) {
    state.focusedShortcutIndex = visibleShortcutCount - 1;
  }
}

const homescreenSlice = createSlice({
  name: "homescreen",
  initialState: INITIAL_STATE,
  reducers: {
    setFocusedCarouselIndex(state, action: PayloadAction<number>): void {
      state.focusedCarouselIndex = action.payload;
      if (state.focusedField === HomescreenFocusedField.CAROUSELS) {
        state.dimmerState = deriveDimmerState(
          state,
          state.focusedField,
          action.payload
        );
      }
    },
    setFocusedTileIndex(
      state,
      action: PayloadAction<{ carouselIndex: number; tileIndex: number }>
    ): void {
      state.focusedTileIndices[action.payload.carouselIndex] =
        action.payload.tileIndex;
    },
    clearCarouselsError(state): void {
      state.carouselsPagination.error = "";
      state.carouselsPagination.errorCode = "";
    },
    setFocusedShortcutIndex(state, action: PayloadAction<number>): void {
      const visibleShortcutCount = getVisibleShortcutCount(state);
      if (visibleShortcutCount === 0) {
        state.focusedShortcutIndex = 0;
        return;
      }
      state.focusedShortcutIndex = Math.max(
        0,
        Math.min(action.payload, visibleShortcutCount - 1)
      );
    },
    focusShortcutLeft(state): void {
      if (state.focusedShortcutIndex > 0) {
        state.focusedShortcutIndex = state.focusedShortcutIndex - 1;
      }
    },
    focusShortcutRight(state): void {
      if (state.focusedShortcutIndex < getVisibleShortcutCount(state) - 1) {
        state.focusedShortcutIndex = state.focusedShortcutIndex + 1;
      }
    },
    setNpvrRecordingAllowed(state, action: PayloadAction<boolean>): void {
      const previousValue = state.isNpvrRecordingAllowed;
      state.isNpvrRecordingAllowed = action.payload;

      const myHubShortcutIndex = getMyHubShortcutIndex(state);
      if (myHubShortcutIndex === -1 || previousValue === action.payload) {
        clampFocusedShortcutIndex(state);
        return;
      }

      if (previousValue !== false && action.payload === false) {
        if (state.focusedShortcutIndex > myHubShortcutIndex) {
          state.focusedShortcutIndex = state.focusedShortcutIndex - 1;
        }
      } else if (previousValue === false && action.payload === true) {
        if (state.focusedShortcutIndex >= myHubShortcutIndex) {
          state.focusedShortcutIndex = state.focusedShortcutIndex + 1;
        }
      }

      clampFocusedShortcutIndex(state);
    },
    setFocusedField(
      state,
      action: PayloadAction<HomescreenFocusedField>
    ): void {
      state.focusedField = action.payload;
      state.dimmerState = deriveDimmerState(
        state,
        action.payload,
        state.focusedCarouselIndex
      );
    },
    resetToDefaultHomescreenState(state): void {
      state.focusedField = HomescreenFocusedField.MENU_STRIP;
      state.focusedCarouselIndex = 0;
      state.dimmerState = HomeScreenDimmerState.SEMI;
    },
    setCarouselsProfileId(state, action: PayloadAction<string>): void {
      state.carouselsProfileId = action.payload;
    },
    resetHomescreenState(state): HomescreenState {
      Log.d(TAG, "resetHomescreenState — clearing carousel and focus state");
      // Preserve shortcuts — they are owned by NavLayout's persistent MenuStrip
      // and must not be cleared on HomeScreen unmount (would trigger a re-fetch).
      return {
        ...INITIAL_STATE,
        isLoadingShortcuts: state.isLoadingShortcuts,
        isShortcutsLoaded: state.isShortcutsLoaded,
        shortcutsError: state.shortcutsError,
        quickMenuShortcuts: state.quickMenuShortcuts,
        isNpvrRecordingAllowed: state.isNpvrRecordingAllowed
      };
    },
    /**
     * Persists carousel and focus state when the home screen unmounts from
     * the carousel field. Preserves pagination data, focus indices, and TTL
     * timestamps for backward stickiness on re-entry. Clears only transient
     * loading/error state so the component starts clean on next mount.
     */
    persistHomeScreenState(state): HomescreenState {
      Log.d(
        TAG,
        "persistHomeScreenState — caching carousel and focus state for backward stickiness"
      );
      return {
        ...state,
        carouselsPagination: {
          ...state.carouselsPagination,
          isLoading: false,
          error: "",
          errorCode: ""
        }
      };
    },
    /**
     * Applies a carousels page produced by the background retry that runs after
     * `loadCarouselsWithRetry` has already rejected.
     *
     * Mirrors the fulfilled reducer and additionally clears `errorCode`, which
     * is what takes the A707 dialog down once the screen can render.
     */
    updateCarouselsFromRetry(
      state,
      action: PayloadAction<InitialCarouselsPayload>
    ): void {
      state.carouselsPagination.items = action.payload.page.items;
      state.carouselsPagination.pageCursor = action.payload.page.pageCursor;
      state.carouselsPagination.isLoading = false;
      state.carouselsPagination.error = "";
      state.carouselsPagination.errorCode = "";
      state.tilePagination = action.payload.tilePagination;
      state.refreshIntervalSecs = action.payload.refreshIntervalSecs;
      state.lastFetchTimestamp = Date.now();
      state.dimmerState = deriveDimmerState(
        state,
        state.focusedField,
        state.focusedCarouselIndex
      );
      Log.i(
        TAG,
        "updateCarouselsFromRetry — background retry recovered " +
          String(state.carouselsPagination.items.length) +
          " rails"
      );
    },
    /**
     * Replaces the A707 code while the background retry is still the owner of
     * the screen state — used when that loop ends on empty predictions, so the
     * dialog reports "nothing to show" instead of the failure it started from.
     */
    updateCarouselsErrorFromRetry(
      state,
      action: PayloadAction<CarouselsErrorPayload>
    ): void {
      state.carouselsPagination.isLoading = false;
      state.carouselsPagination.error = action.payload.message;
      state.carouselsPagination.errorCode = action.payload.errorCode;
    },
    updateQuickMenuShortcutsFromRetry(
      state,
      action: PayloadAction<QuickMenuShortcutV4[]>
    ): void {
      state.quickMenuShortcuts = action.payload.filter(isSupportedShortcut);
      state.isShortcutsLoaded = true;
      state.shortcutsError = "";
      clampFocusedShortcutIndex(state);
    }
  },
  extraReducers: (builder) => {
    // Load more carousels (append)
    builder
      .addCase(loadMoreCarousels.pending, (state): void => {
        state.carouselsPagination.isLoading = true;
        state.carouselsPagination.error = "";
      })
      .addCase(
        loadMoreCarousels.fulfilled,
        (state, action: PayloadAction<InitialCarouselsPayload>): void => {
          applyCursorPage(state.carouselsPagination, action.payload.page);
          // Merge new tile pagination entries (don't overwrite existing)
          Object.assign(
            state.tilePagination,
            Object.fromEntries(
              Object.entries(action.payload.tilePagination).filter(
                ([key]) => state.tilePagination[key] === undefined
              )
            )
          );
          // Update refresh TTL from latest response
          state.refreshIntervalSecs = action.payload.refreshIntervalSecs;
          state.lastFetchTimestamp = Date.now();
          Log.d(
            TAG,
            "loadMoreCarousels.fulfilled — total carousels: " +
              String(state.carouselsPagination.items.length)
          );
        }
      )
      .addCase(loadMoreCarousels.rejected, (state, action): void => {
        state.carouselsPagination.isLoading = false;
        state.carouselsPagination.error =
          action.payload === undefined ? UNKNOWN_ERROR_MESSAGE : action.payload;
      });

    // Load more tiles for a specific rail (append)
    builder
      .addCase(loadMoreTiles.pending, (state, action): void => {
        const tileState = state.tilePagination[action.meta.arg.carouselId];
        if (tileState !== undefined) {
          tileState.isLoading = true;
          tileState.error = "";
        }
      })
      .addCase(
        loadMoreTiles.fulfilled,
        (state, action: PayloadAction<TLoadMoreTilesPayload>): void => {
          const tileState = state.tilePagination[action.payload.carouselId];
          if (tileState !== undefined) {
            applyOffsetPage(tileState, action.payload.page);
            Log.d(
              TAG,
              "loadMoreTiles.fulfilled — carouselId=" +
                action.payload.carouselId +
                ", total tiles: " +
                String(tileState.items.length)
            );
          }
        }
      )
      .addCase(loadMoreTiles.rejected, (state, action): void => {
        const tileState = state.tilePagination[action.meta.arg.carouselId];
        if (tileState !== undefined) {
          tileState.isLoading = false;
          tileState.error =
            action.payload === undefined
              ? UNKNOWN_ERROR_MESSAGE
              : action.payload;
        }
      });

    // Shortcut thunk
    // `isLoadingShortcuts` is an in-flight marker only — it drives the thunk's
    // `condition` guard so overlapping callers collapse onto one request. No
    // view renders a loading state from it, so setting it here cannot cause
    // the "Loading menu…" flash the previous implementation avoided.
    builder
      .addCase(fetchQuickMenuShortcuts.pending, (state) => {
        state.isLoadingShortcuts = true;
        state.shortcutsError = "";
      })
      .addCase(fetchQuickMenuShortcuts.fulfilled, (state, action) => {
        state.isLoadingShortcuts = false;
        state.isShortcutsLoaded = true;
        state.quickMenuShortcuts = action.payload.filter(isSupportedShortcut);
        clampFocusedShortcutIndex(state);
      })
      .addCase(fetchQuickMenuShortcuts.rejected, (state, action) => {
        state.isLoadingShortcuts = false;
        state.isShortcutsLoaded = false;
        state.shortcutsError =
          action.payload === undefined
            ? "Failed to fetch quick-menu shortcuts"
            : action.payload;
      });

    // loadCarouselsWithRetry (screens/recommendations with CRITICAL retry)
    builder
      .addCase(loadCarouselsWithRetry.pending, (state): void => {
        state.carouselsPagination.isLoading = true;
        state.carouselsPagination.error = "";
        state.carouselsPagination.errorCode = "";
      })
      .addCase(
        loadCarouselsWithRetry.fulfilled,
        (state, action: PayloadAction<InitialCarouselsPayload>): void => {
          state.carouselsPagination.items = action.payload.page.items;
          state.carouselsPagination.pageCursor = action.payload.page.pageCursor;
          state.carouselsPagination.isLoading = false;
          state.carouselsPagination.error = "";
          state.carouselsPagination.errorCode = "";
          state.tilePagination = action.payload.tilePagination;
          state.refreshIntervalSecs = action.payload.refreshIntervalSecs;
          state.lastFetchTimestamp = Date.now();
          state.dimmerState = deriveDimmerState(
            state,
            state.focusedField,
            state.focusedCarouselIndex
          );
          Log.d(
            TAG,
            "loadCarouselsWithRetry.fulfilled — " +
              String(state.carouselsPagination.items.length) +
              " rails, hasMore=" +
              String(action.payload.page.pageCursor !== null)
          );
        }
      )
      .addCase(loadCarouselsWithRetry.rejected, (state, action): void => {
        // Ignore abort-driven rejections (empty message = intentional cancellation)
        const hasPayload: boolean =
          action.payload !== null && action.payload !== undefined;
        const message: string = hasPayload
          ? (action.payload as CarouselsErrorPayload).message
          : UNKNOWN_ERROR_MESSAGE;
        if (message === "") {
          state.carouselsPagination.isLoading = false;
          return;
        }
        // The background retry can land before this rejection is dispatched.
        // A707 reports an empty Home Screen, so once there are rails on screen
        // the failure that preceded them is history — never re-raise it.
        if (state.carouselsPagination.items.length > 0) {
          state.carouselsPagination.isLoading = false;
          Log.d(
            TAG,
            "loadCarouselsWithRetry.rejected — carousels already recovered, not reporting"
          );
          return;
        }
        state.carouselsPagination.isLoading = false;
        state.carouselsPagination.error = message;
        state.carouselsPagination.errorCode = hasPayload
          ? (action.payload as CarouselsErrorPayload).errorCode
          : HOMESCREEN_ERROR_CODE;
      });
  }
});

export const {
  setFocusedCarouselIndex,
  setFocusedTileIndex,
  clearCarouselsError,
  setFocusedShortcutIndex,
  focusShortcutLeft,
  focusShortcutRight,
  setNpvrRecordingAllowed,
  setFocusedField,
  resetHomescreenState,
  resetToDefaultHomescreenState,
  setCarouselsProfileId,
  updateCarouselsFromRetry,
  updateCarouselsErrorFromRetry,
  updateQuickMenuShortcutsFromRetry,
  persistHomeScreenState
} = homescreenSlice.actions;

/**
 * TTL-guarded refresh thunk.
 *
 * Skips the request if data is still fresh (elapsed < TTL) or a fetch is
 * already in flight. Called on foreground restore and network reconnect.
 * Data fetched for a different profile is never fresh: a profile switch
 * returns to Home well inside the TTL, and must not show the previous
 * profile's carousels.
 *
 * When the TTL has expired, resets the entire homescreen state to its initial
 * values (preserving shortcuts) so the screen behaves like a fresh mount —
 * skeleton shows immediately and focus/scroll start from scratch.
 *
 * Delegates to loadCarouselsWithRetry so that network errors surface
 * the overlay and server errors are retried with CRITICAL policy.
 * The caller must provide an AbortSignal to allow cancellation.
 */
export const refreshCarousels =
  (signal: AbortSignal) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const { homescreen, profiles } = getState();
    const elapsed: number = Date.now() - homescreen.lastFetchTimestamp;
    const ttl: number = homescreen.refreshIntervalSecs * 1000;
    const activeProfileId: string = profiles.selectedProfileId;
    const isSameProfile: boolean =
      homescreen.carouselsProfileId === activeProfileId;

    if (
      homescreen.carouselsPagination.isLoading ||
      (elapsed < ttl && isSameProfile)
    ) {
      Log.i(TAG, "refreshCarousels() - data still fresh or loading, skipping");
      return;
    }
    if (!isSameProfile) {
      Log.i(TAG, "refreshCarousels() - active profile changed, re-fetching");
    }

    Log.i(
      TAG,
      "refreshCarousels() - data stale, resetting state and dispatching loadCarouselsWithRetry"
    );
    dispatch(resetHomescreenState());
    dispatch(setCarouselsProfileId(activeProfileId));
    dispatch(loadCarouselsWithRetry({ signal }));
  };

/**
 * Fetches quick-menu shortcuts from the API with automatic
 * fallback to cached data or hardcoded defaults.
 * Defined after the slice so it can reference the slice action
 * updateQuickMenuShortcutsFromRetry directly without createAction.
 */
export const fetchQuickMenuShortcuts = createAsyncThunk<
  QuickMenuShortcutV4[],
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "homescreen/fetchQuickMenuShortcuts",
  async (_: void, { rejectWithValue, dispatch, signal }) => {
    try {
      Log.d(TAG, "fetchQuickMenuShortcuts() starting");
      const interactor: HomeScreenInteractor = new HomeScreenInteractor();
      const onRetrySuccess = (retried: QuickMenuShortcutV4[]): void => {
        Log.i(
          TAG,
          `fetchQuickMenuShortcuts() background retry succeeded — updating Redux store with ${retried.length} shortcuts`
        );
        dispatch(updateQuickMenuShortcutsFromRetry(retried));
      };
      const shortcuts: QuickMenuShortcutV4[] =
        await interactor.loadMenuShortcutsAtAppLaunch(
          {},
          signal,
          onRetrySuccess
        );
      Log.d(
        TAG,
        `fetchQuickMenuShortcuts() SUCCESS - loaded ${shortcuts.length} shortcuts`
      );
      return shortcuts;
    } catch (error: unknown) {
      Log.e(TAG, "fetchQuickMenuShortcuts() FAILED", error);
      const message =
        error instanceof Error ? error.message : "Failed to fetch shortcuts";
      QuickMenuOperationalLogger.logApiFailure(error);
      QuickMenuOperationalLogger.logUiError({
        errorMessage: message,
        screen: HOMESCREEN_CAROUSEL_SCREEN_NAME,
        screenId: HOMESCREEN_CAROUSEL_SCREEN_ID
      });
      DiagnosticLogger.log(
        QUICK_MENU_DIAGNOSTIC_EVENT,
        "Quick Menu shortcuts could not be loaded",
        DiagnosticSeverity.Error,
        {
          event: "load_failed",
          error_type: OperationalLogger.describeErrorType(error)
        }
      );
      return rejectWithValue(message);
    }
  },
  {
    // postSigninStartupSequence prefetches the shortcuts and NavLayout asks
    // for them again when it mounts on the first protected screen. NavLayout
    // can only see `isShortcutsLoaded`, which flips on fulfilled, so without
    // this guard the two overlap into parallel requests. Neither caller reads
    // the return value — both render from the store — so skipping is safe.
    condition: (_arg: void, { getState }): boolean => {
      const state = getState().homescreen;
      return !state.isLoadingShortcuts && !state.isShortcutsLoaded;
    }
  }
);

// ─── Selectors ───

// Backward-compatible flat selectors (project from new shape)
export const selectCarousels = (state: UnityRootState): TCarouselRail[] =>
  state.homescreen.carouselsPagination.items;

export const selectIsLoadingCarousels = (state: UnityRootState): boolean =>
  state.homescreen.carouselsPagination.isLoading;

export const selectCarouselsError = (state: UnityRootState): string =>
  state.homescreen.carouselsPagination.error;

/**
 * Composite catalog code for the Home Screen error **dialog** — `"A707-G500"`,
 * or `"A707"` when there is no sub-error code to show. Empty string when the
 * screen has no error to report, including an intentional abort.
 *
 * Drives a dialog overlay, not a whisper.
 */
export const selectHomeScreenErrorCode = (state: UnityRootState): string => {
  // Read defensively: `errorCode` is newer than the persisted state shape, so
  // a session restored from an older build (or any partial fixture) can reach
  // this selector without it.
  const errorCode: string | undefined =
    state.homescreen.carouselsPagination.errorCode;
  return errorCode === undefined || errorCode === null ? "" : errorCode;
};

// New pagination selectors
export const selectCarouselsPagination = (
  state: UnityRootState
): TCarouselRailPaginationState => state.homescreen.carouselsPagination;

export const selectIsLoadingMoreCarousels = (
  state: UnityRootState
): boolean => {
  const p = state.homescreen.carouselsPagination;
  return p.isLoading && p.items.length > 0;
};

export const selectTilePagination =
  (carouselId: string) =>
  (state: UnityRootState): TTilePaginationState | undefined =>
    state.homescreen.tilePagination[carouselId];

export const selectAllTilePagination = (
  state: UnityRootState
): Record<string, TTilePaginationState> => state.homescreen.tilePagination;

export const selectFocusedCarouselIndex = (state: UnityRootState): number =>
  state.homescreen.focusedCarouselIndex;

export const selectFocusedTileIndices = (
  state: UnityRootState
): Partial<Record<number, number>> => state.homescreen.focusedTileIndices;

export const selectRefreshIntervalSecs = (state: UnityRootState): number =>
  state.homescreen.refreshIntervalSecs;

export const selectLastFetchTimestamp = (state: UnityRootState): number =>
  state.homescreen.lastFetchTimestamp;

// Shortcut selectors
export const selectIsLoadingShortcuts = (state: UnityRootState): boolean =>
  state.homescreen.isLoadingShortcuts;

export const selectIsShortcutsLoaded = (state: UnityRootState): boolean =>
  state.homescreen.isShortcutsLoaded;

export const selectShortcutsError = (state: UnityRootState): string =>
  state.homescreen.shortcutsError;

export const selectQuickMenuShortcuts = (
  state: UnityRootState
): QuickMenuShortcutV4[] => getVisibleQuickMenuShortcuts(state.homescreen);

export const selectFocusedShortcutIndex = (state: UnityRootState): number =>
  state.homescreen.focusedShortcutIndex;

export const selectHomescreenFocusedField = (
  state: UnityRootState
): HomescreenFocusedField => state.homescreen.focusedField;

export const selectDimmerState = (
  state: UnityRootState
): HomeScreenDimmerState => state.homescreen.dimmerState;

export default homescreenSlice.reducer;
